<template>
    <component
        :is="stepComponent"
        :form="form"
        :errors="errors"
        :registration-enabled="registrationEnabled"
        @go-to-next-step="goToNextStep($event)"
        @submit-form="submitForm"
        @invalidate="invalidate"
        @validate="validate"
    />
    <Meta title="Register" description="Register for a SECLOCK.com account" />
</template>

<script setup>
import { inject, markRaw, ref } from "vue";
import { eventBus, useRecaptcha } from "@/Composables";
import StepOne from "./StepOne.vue";
import WebAccessForm from "./WebAccessForm.vue";
import RegistrationStep1 from "./RegistrationStep1.vue";
import RegistrationStep2 from "./RegistrationStep2.vue";
import RegistrationStep3 from "./RegistrationStep3.vue";
import { router, useForm } from "@inertiajs/vue3";
import { Meta, ServiceUnavailableNotice } from "@/Components";

const props = defineProps([
    "registrationEnabled",
]);

const route = inject("route");
const { getRecaptchaToken } = useRecaptcha();

const form = useForm({
    how_hear_about_us: "", //bound to a select : has to be '' a null value would break the SeclockInput label/default value
    company_name: "",
    phone: "",
    extension: "",
    phone_type: "", //bound to a select
    company_website: "",
    business_type: "", //bound to a select
    referred_by: null,

    first_name: "",
    last_name: "",
    title: "",
    email: "",
    email_for_invoices: "",
    password: "",
    password_confirmation: "",

    billing_address: null,
    billing_address_2: null,
    city: null,
    state: "", //bound to a select
    zipcode: null,
    country: "", //bound to a select

    new_account: false,
    account_number: null,
    recaptcha: "",
});

const stepsList = {
    StepOne: markRaw(StepOne),
    WebAccessForm: markRaw(WebAccessForm),
    RegistrationStep1: markRaw(RegistrationStep1),
    RegistrationStep2: markRaw(RegistrationStep2),
    RegistrationStep3: markRaw(RegistrationStep3),
};

const selectedOption = ref(null);
const accountOptions = ref([
    {
        option: "Yes",
        extended: "my company has an account",
    },
    {
        option: "No",
        extended: "my company has no account",
    },
]);

const stepComponent = ref(stepsList.StepOne);

const errors = ref(false);

function invalidate() {
    errors.value = true;
}

function validate() {
    errors.value = false;
}

function goToNextStep(event) {
    window.scrollTo(0, 0);
    stepComponent.value = stepsList[event.step];
}

const submitForm = async (selectedPlace) => {
    if(selectedPlace?.address) {
        form.billing_address = selectedPlace?.address;
        form.billing_address_2 = selectedPlace?.address_2;
        form.city = selectedPlace?.city;
        form.state = selectedPlace?.state;
        form.zipcode = selectedPlace?.zip;
        form.country = selectedPlace?.country;
    }
    form.recaptcha = await getRecaptchaToken("register");
    form.submit("post", route("register"), {
        preserveScroll: true,
        onSuccess: () => {
            if(form.new_account) {
                router.visit(route('web.account-pending'));
            } else {
                window.scrollTo(0, 0);
                form.reset()
                stepComponent.value = stepsList.StepOne
                errors.value = false
            }
        },
        onError: (error) => {
            invalidate();
            let error_message =
                Object.values(error)[0] ?? "Error processing submission";
            eventBus.emit("alert:show", {
                title: "Error",
                message: error_message,
                level: "danger",
            });
        },
    });
}
</script>
